import type { ClicktermDialogTranslations, ClicktermDialogTranslationsMap } from './models';

const translationPath = '/sdk/clickterm-widget-translations.json';
const defaultTranslationsBaseUrl = 'https://app.clickterm.com';

export const defaultDialogTranslations: ClicktermDialogTranslations = {
  publishedOn: 'Published on',
  version: 'Version',
  accept: 'Accept',
  decline: 'Decline',
  checkboxLabelText: 'I agree to the [ClickwrapTitle].',
  checkboxDescription: 'By clicking Accept, you agree to be bound by the [ClickwrapTitle].',
  scrollTextTitle: 'Scroll to the end to enable the Accept button.',
  scrollTextSubtitle: 'By clicking Accept, you agree to be bound by the [ClickwrapTitle].',
  scroll: 'Scroll',
  cancel: 'Cancel',
  close: 'Close',
  acceptedAt: 'Accepted at',
};

let translations: ClicktermDialogTranslationsMap | null = null;
let translationsPromise: Promise<void> | null = null;
let translationsUrl: string | null = null;

const normalizeLanguage = (language?: string): string => {
  const normalized = language?.trim().toLowerCase();
  return normalized && normalized.length > 0 ? normalized : 'en';
};

const resolveTranslationEntry = (language?: string): ClicktermDialogTranslations => {
  if (!translations) {
    return defaultDialogTranslations;
  }
  const normalized = normalizeLanguage(language);
  const candidates = normalized === 'en' ? ['en'] : [normalized, 'en'];
  for (const key of candidates) {
    const entry = translations[key];
    if (entry) {
      return { ...defaultDialogTranslations, ...entry };
    }
  }
  return defaultDialogTranslations;
};

const resolveTranslationsBaseUrl = (baseUrl: string): string => {
  try {
    const parsed = new URL(baseUrl);
    if (parsed.hostname.startsWith('api.')) {
      parsed.hostname = parsed.hostname.replace(/^api\./, 'app.');
    }
    return parsed.origin;
  } catch (_) {
    return defaultTranslationsBaseUrl;
  }
};

const buildTranslationsUrl = (baseUrl: string): string => {
  try {
    return new URL(translationPath, resolveTranslationsBaseUrl(baseUrl)).toString();
  } catch (_) {
    return translationPath;
  }
};

export const startTranslationsLoad = (baseUrl: string): void => {
  const url = buildTranslationsUrl(baseUrl);
  if (translationsPromise && translationsUrl === url) {
    return;
  }
  translationsUrl = url;
  translationsPromise = fetch(url)
    .then(response => {
      if (!response.ok) {
        throw new Error(`Failed to load translations: ${response.status}`);
      }
      return response.json();
    })
    .then((data: unknown) => {
      if (data && typeof data === 'object') {
        translations = data as ClicktermDialogTranslationsMap;
      }
    })
    .catch(() => {
      // Keep defaults if translations are unavailable.
      console.error('Could not load clickterm dialog translations, using defaults.');
    });
};

export const getDialogTranslations = async (language?: string): Promise<ClicktermDialogTranslations> => {
  if (translationsPromise) {
    try {
      await translationsPromise;
    } catch (_) {
      // Ignore translation load failures and fall back to defaults.
    }
  }
  return resolveTranslationEntry(language);
};
